/**
 * AlertsPage — live engine alerts feed.
 *
 * Aggregates: unverified fires, high EC, soil drought / saturation,
 * stale sensors, schedule errors, low forecast trust. Each row is
 * severity-tagged + deep-linkable + individually dismissible (30 days)
 * with a "Dismiss all" bulk action.
 *
 * Same auth + nav pattern as TurfPulseDashboard.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, RefreshCw, Home, BarChart3, Clock, Settings as SettingsIcon,
  AlertTriangle, AlertCircle, Info, ChevronRight, X, CheckCheck,
} from 'lucide-react';
import { API_URL } from '../config/api';

const API = API_URL;

const SEV_STYLE = {
  high:   { border: 'border-rose-700/60',   pill: 'bg-rose-500/20 text-rose-300',   Icon: AlertTriangle, iconClass: 'text-rose-400' },
  medium: { border: 'border-amber-700/60',  pill: 'bg-amber-500/20 text-amber-300', Icon: AlertCircle,   iconClass: 'text-amber-400' },
  low:    { border: 'border-slate-700/60',  pill: 'bg-slate-500/20 text-slate-300', Icon: Info,           iconClass: 'text-slate-400' },
};

function AlertRow({ alert, onDismiss, onOpen }) {
  const sty = SEV_STYLE[alert.severity] || SEV_STYLE.low;
  const Icon = sty.Icon;
  return (
    <div
      data-testid={`alert-row-${alert.kind}`}
      className={`bg-slate-900/80 border ${sty.border} rounded-2xl p-4 transition-colors hover:border-emerald-600/60`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${sty.iconClass}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${sty.pill}`}>
              {alert.severity}
            </span>
            <span className="text-[10px] text-slate-500">{alert.kind.replace(/_/g, ' ')}</span>
          </div>
          <div className="text-sm font-semibold text-slate-100 leading-tight" data-testid="alert-title">{alert.title}</div>
          <div className="text-xs text-slate-400 mt-1 leading-snug">{alert.subhead}</div>
          <div className="flex items-center justify-between mt-3">
            <button
              data-testid="alert-action-btn"
              onClick={() => onOpen(alert)}
              className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 font-medium"
            >
              {alert.action_label} <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid="alert-dismiss-btn"
              onClick={() => onDismiss(alert.id)}
              className="text-[10px] text-slate-500 hover:text-slate-300 flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AlertsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/alerts/now`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  const dismiss = async (id) => {
    // Optimistic — pull row before the network round-trip
    setData((d) => d ? ({ ...d, alerts: d.alerts.filter(a => a.id !== id) }) : d);
    try {
      await fetch(`${API}/api/alerts/dismiss`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alert_id: id }),
      });
    } catch {/* tolerate failure — next refresh will reconcile */}
  };

  const dismissAll = async () => {
    setData((d) => d ? ({ ...d, alerts: [] }) : d);
    try {
      await fetch(`${API}/api/alerts/dismiss-all`, { method: 'POST', credentials: 'include' });
    } catch {/* noop */}
  };

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const alerts = data?.alerts || [];
  const summary = data?.summary || { high: 0, medium: 0, low: 0, total: 0 };

  return (
    <div className="min-h-screen bg-black text-slate-100 pb-20">
      <div className="max-w-md mx-auto px-4 pt-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-6 h-6 text-amber-400" />
            <div className="text-xl font-bold">Alerts</div>
            {summary.total > 0 && (
              <span className="ml-1 text-xs bg-amber-500/20 text-amber-300 rounded-full px-2 py-0.5 font-semibold" data-testid="alerts-count-badge">
                {summary.total}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {alerts.length > 0 && (
              <button
                data-testid="alerts-dismiss-all-btn"
                onClick={dismissAll}
                className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-slate-800 transition-colors"
              >
                <CheckCheck className="w-4 h-4" /> Clear
              </button>
            )}
            <button
              data-testid="alerts-refresh-btn"
              onClick={fetchAlerts}
              className="p-2 rounded-full hover:bg-slate-800 transition-colors"
            >
              <RefreshCw className={`w-5 h-5 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Severity summary */}
        {summary.total > 0 && (
          <div className="grid grid-cols-3 gap-2 text-center" data-testid="alerts-severity-summary">
            <div className="bg-rose-950/40 border border-rose-900/60 rounded-xl p-2">
              <div className="text-lg font-bold text-rose-300">{summary.high}</div>
              <div className="text-[10px] uppercase tracking-wider text-rose-400">High</div>
            </div>
            <div className="bg-amber-950/40 border border-amber-900/60 rounded-xl p-2">
              <div className="text-lg font-bold text-amber-300">{summary.medium}</div>
              <div className="text-[10px] uppercase tracking-wider text-amber-400">Medium</div>
            </div>
            <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-2">
              <div className="text-lg font-bold text-slate-300">{summary.low}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Low</div>
            </div>
          </div>
        )}

        {/* Body */}
        {error && (
          <div data-testid="alerts-error" className="bg-rose-950/40 border border-rose-900 rounded-xl p-3 text-xs text-rose-300">
            Failed to load: {error}
          </div>
        )}

        {/* Moisture Alerts CTA — Phase 1: per-zone push thresholds */}
        <button
          data-testid="moisture-alerts-cta"
          onClick={() => navigate('/moisture-alerts')}
          className="w-full bg-gradient-to-br from-emerald-900/40 to-slate-900/60 border border-emerald-800/60 rounded-2xl p-4 flex items-center justify-between hover:border-emerald-600/60 transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Bell className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-100">Moisture Alerts</div>
              <div className="text-[11px] text-slate-400">Per-zone push at 45% & 42% thresholds</div>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </button>

        {loading && !data ? (
          <div className="text-center text-slate-400 py-10">Loading alerts…</div>
        ) : alerts.length === 0 ? (
          <div data-testid="alerts-empty" className="text-center py-12 text-slate-400">
            <CheckCheck className="w-10 h-10 mx-auto text-emerald-500/60 mb-3" />
            <div className="text-sm font-medium">All clear</div>
            <div className="text-xs mt-1">The engine has nothing urgent to flag.</div>
          </div>
        ) : (
          <div className="space-y-2.5">
            {alerts.map((a) => (
              <AlertRow
                key={a.id}
                alert={a}
                onDismiss={dismiss}
                onOpen={(al) => navigate(al.action_route)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Bottom nav — same layout as dashboard, leaves room for the
          Emergent watermark on hosted deploys. */}
      <div className="fixed bottom-0 inset-x-0 bg-slate-950/90 backdrop-blur border-t border-slate-800 z-30">
        <div className="max-w-[calc(100%-185px)] px-2 py-2 grid grid-cols-5 text-center text-[10px] text-slate-400">
          <button data-testid="nav-home"     onClick={() => navigate('/pulse')}    className="flex flex-col items-center gap-0.5 py-1"><Home className="w-5 h-5"/>Home</button>
          <button data-testid="nav-history"  onClick={() => navigate('/activity')} className="flex flex-col items-center gap-0.5 py-1"><Clock className="w-5 h-5"/>History</button>
          <button data-testid="nav-trends"   onClick={() => navigate('/trends')}   className="flex flex-col items-center gap-0.5 py-1"><BarChart3 className="w-5 h-5"/>Trends</button>
          <button data-testid="nav-alerts"                                         className="flex flex-col items-center gap-0.5 py-1 text-emerald-400"><Bell className="w-5 h-5"/>Alerts</button>
          <button data-testid="nav-settings" onClick={() => navigate('/profile')}  className="flex flex-col items-center gap-0.5 py-1"><SettingsIcon className="w-5 h-5"/>Settings</button>
        </div>
      </div>
    </div>
  );
}
